(() => {
  "use strict";
  if (customElements.get("ppa-browse")) return;

  function listedPrice(product) {
    if (Number.isSafeInteger(product.price) && product.price > 0)
      return product.price;
    const value = String(product.gallery || "")
      .trim()
      .replace(/^USD\s*/i, "")
      .replace(/\s*USD$/i, "")
      .replace(/^\$\s*/, "");
    // Reject ambiguous ranges and prose instead of turning them into cheap works.
    if (!/^(?:\d+|\d{1,3}(?:,\d{3})+)(?:\.\d{1,2})?$/.test(value)) return null;
    const cents = Math.round(Number(value.replace(/,/g, "")) * 100);
    return cents > 0 && Number.isSafeInteger(cents) ? cents : null;
  }

  function inBudget(price, budget) {
    if (budget === "all") return true;
    if (budget === "on-request") return price === null;
    if (price === null) return false;
    if (budget === "under-2500") return price < 250000;
    if (budget === "2500-10000") return price >= 250000 && price <= 1000000;
    if (budget === "over-10000") return price > 1000000;
    return false;
  }

  class PPABrowse extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.section = this.closest('[data-section-type="collection"]');
      this.results = this.section.querySelector(".CollectionInner__Products");
      this.budget = this.querySelector("[data-budget]");
      this.sort = this.querySelector("[data-sort]");
      this.status = this.querySelector("[data-status]");
      const grid = this.results.querySelector(".ProductList");
      this.gridClass = grid?.className || "ProductList ProductList--grid Grid";
      this.cellClass =
        grid?.querySelector(".Grid__Cell")?.className ||
        "Grid__Cell 1/2--phone 1/3--tablet-and-up 1/4--desk";
      this.mobile = grid?.dataset.mobileCount || "2";
      this.desktop = grid?.dataset.desktopCount || "4";
      this.cache = new Map();
      const url = new URL(location.href);
      this.budget.value = url.searchParams.get("ppa_budget") || "all";
      if (!this.budget.value) this.budget.value = "all";
      this.sort.value =
        url.searchParams.get("sort_by") || this.dataset.defaultSort;
      this.budget.addEventListener("change", () => this.change());
      this.sort.addEventListener("change", () => this.change());
      this.querySelector("[data-reset]").addEventListener("click", () => {
        const next = new URL(location.href);
        for (const key of [...next.searchParams.keys()]) {
          if (
            key.startsWith("filter.") ||
            key.startsWith("ppa_") ||
            key === "page" ||
            key === "sort_by"
          )
            next.searchParams.delete(key);
        }
        location.assign(next);
      });
      // Native artist filters normally replace the grid with an AJAX response.
      // Navigate with both filter states so a late response cannot overwrite a budget result.
      this.filterHandler = (event) => {
        const form = event.target.closest(".collection-filters-form");
        if (!form || !this.section.contains(form)) return;
        event.stopImmediatePropagation();
        const next = new URL(location.href);
        for (const key of [...next.searchParams.keys()])
          if (key.startsWith("filter.") || key === "page" || key === "ppa_page")
            next.searchParams.delete(key);
        const formData = new FormData(form);
        for (const [key, value] of formData)
          if (key.startsWith("filter.") && !key.startsWith("filter.v.price."))
            next.searchParams.append(key, value);
        next.searchParams.set("sort_by", this.sort.value);
        location.assign(next);
      };
      document.addEventListener("change", this.filterHandler, true);
      this.clearHandler = (event) => {
        const button = event.target.closest('[data-action="clear-filters"]');
        if (!button || !this.section.contains(button)) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        location.assign(button.dataset.url);
      };
      document.addEventListener("click", this.clearHandler, true);
      this.popHandler = () => location.reload();
      window.addEventListener("popstate", this.popHandler);
      if (this.needsIndex()) this.update();
    }

    disconnectedCallback() {
      this.abort?.abort();
      document.removeEventListener("change", this.filterHandler, true);
      document.removeEventListener("click", this.clearHandler, true);
      window.removeEventListener("popstate", this.popHandler);
    }

    needsIndex() {
      return (
        this.budget.value !== "all" || this.sort.value.startsWith("price-")
      );
    }

    change() {
      const url = new URL(location.href);
      url.searchParams.delete("page");
      url.searchParams.delete("ppa_page");
      url.searchParams.delete("filter.v.price.gte");
      url.searchParams.delete("filter.v.price.lte");
      if (this.budget.value === "all") url.searchParams.delete("ppa_budget");
      else url.searchParams.set("ppa_budget", this.budget.value);
      url.searchParams.set("sort_by", this.sort.value);
      if (!this.needsIndex()) {
        location.assign(url);
        return;
      }
      history.pushState({}, "", url);
      this.update();
    }

    async loadIndex(signal) {
      const url = new URL(this.dataset.collection, location.origin);
      const current = new URL(location.href);
      for (const [key, value] of current.searchParams) {
        if (
          (key.startsWith("filter.") && !key.startsWith("filter.v.price.")) ||
          key === "q" ||
          key === "preview_theme_id"
        )
          url.searchParams.append(key, value);
      }
      url.searchParams.set(
        "sort_by",
        this.sort.value.startsWith("price-")
          ? "title-ascending"
          : this.sort.value,
      );
      url.searchParams.set("view", "ppa-browse-index-v2");
      const key = url.toString();
      if (this.cache.has(key)) return this.cache.get(key);
      const getPage = async (page) => {
        const request = new URL(url);
        request.searchParams.set("page", String(page));
        const response = await fetch(request, {
          signal,
          credentials: "same-origin",
        });
        if (!response.ok) throw new Error("Catalog request failed");
        const data = await response.json();
        if (
          !Array.isArray(data.products) ||
          data.page !== page ||
          !Number.isSafeInteger(data.count) ||
          data.count < 0 ||
          !Number.isSafeInteger(data.pages) ||
          data.pages < 0 ||
          (data.count > 0 && data.pages < 1) ||
          data.products.length > 250
        )
          throw new Error("Invalid catalog page");
        if (data.currency !== "USD")
          throw new Error("USD browsing is unavailable in this currency");
        return data;
      };
      const first = await getPage(1);
      if (first.count > 25000 || first.pages > 100)
        throw new Error("Collection exceeds supported index size");
      const products = [...first.products];
      for (let page = 2; page <= first.pages; page += 6) {
        const batch = await Promise.all(
          Array.from({ length: Math.min(6, first.pages - page + 1) }, (_, i) =>
            getPage(page + i),
          ),
        );
        for (const part of batch) {
          if (part.count !== first.count || part.pages !== first.pages)
            throw new Error("Catalog changed during loading");
          products.push(...part.products);
          this.status.textContent = `Preparing prices… ${products.length.toLocaleString()} of ${first.count.toLocaleString()} works`;
        }
      }
      if (
        products.length !== first.count ||
        new Set(products.map((p) => p.id)).size !== first.count
      )
        throw new Error("Incomplete catalog");
      const normalized = products.map((p) => ({
        ...p,
        listed: listedPrice(p),
      }));
      this.cache.set(key, normalized);
      return normalized;
    }

    async update() {
      this.abort?.abort();
      const controller = (this.abort = new AbortController());
      this.results.classList.add("PPABrowse__Results");
      this.results.setAttribute("aria-busy", "true");
      this.status.textContent = "Finding artwork across the collection…";
      try {
        const products = await this.loadIndex(controller.signal);
        if (controller.signal.aborted) return;
        this.filtered = products.filter((p) =>
          inBudget(p.listed, this.budget.value),
        );
        if (this.sort.value.startsWith("price-")) {
          const direction = this.sort.value === "price-descending" ? -1 : 1;
          this.filtered.sort((a, b) => {
            if (a.listed === null && b.listed !== null) return 1;
            if (b.listed === null && a.listed !== null) return -1;
            return (
              direction * ((a.listed || 0) - (b.listed || 0)) ||
              a.title.localeCompare(b.title) ||
              a.id - b.id
            );
          });
        }
        this.render(
          Number(new URL(location.href).searchParams.get("ppa_page")) || 1,
        );
      } catch (error) {
        if (controller.signal.aborted) return;
        // Do not silently present a partial page as a complete filtered collection.
        this.results.replaceChildren();
        const message = document.createElement("p");
        message.className = "PPABrowse__Error";
        message.textContent =
          "We couldn’t load these results. Please try again or clear the filters to browse all artwork.";
        const retry = document.createElement("button");
        retry.type = "button";
        retry.className = "Button Button--secondary";
        retry.textContent = "Try again";
        retry.addEventListener("click", () => this.update());
        message.append(document.createElement("br"), retry);
        this.results.append(message);
        this.status.textContent = "Results unavailable.";
      } finally {
        if (!controller.signal.aborted)
          this.results.removeAttribute("aria-busy");
      }
    }

    card(product) {
      const el = (tag, className, text) => {
        const node = document.createElement(tag);
        node.className = className;
        if (text != null) node.textContent = text;
        return node;
      };
      const link = (className, text) => {
        const node = el("a", className, text);
        node.href = product.url;
        return node;
      };
      const item = el(
        "div",
        "ProductItem" + (product.inquiry === "true" ? " ProductItem--por" : ""),
      );
      const wrapper = el("div", "ProductItem__Wrapper");
      item.append(wrapper);
      const picture = link("ProductItem__ImageWrapper");
      const ratio = el("div", "AspectRatio");
      const image = el("img", "ProductItem__Image");
      image.src = product.image;
      image.alt = product.alt || product.title;
      image.loading = "lazy";
      image.decoding = "async";
      image.width = 600;
      image.height = 600;
      ratio.append(image);
      picture.append(ratio);
      wrapper.append(picture);
      if (product.sold) {
        const labels = el("div", "ProductItem__LabelList");
        labels.append(
          el(
            "span",
            "ProductItem__Label ProductItem__Label--soldOut Heading Text--subdued",
            "Sold out",
          ),
        );
        wrapper.append(labels);
      }
      const info = el("div", "ProductItem__Info ProductItem__Info--left");
      info.append(el("p", "ProductItem__Vendor Heading", product.vendor));
      const title = el("h2", "ProductItem__Title Heading");
      const titleLink = link("");
      // The display string is escaped by our Liquid title renderer, not authored HTML.
      titleLink.textContent = window.PPASafeDOM.fragment(product.display).textContent;
      title.append(titleLink);
      info.append(title);
      info.append(
        link(
          "PPACardAction",
          (product.inquiry === "true"
            ? listedPrice(product) === null
              ? "Request price"
              : "Inquire"
            : "View work") + " ↗",
        ),
      );
      if (product.medium) info.append(el("p", "PPACardDetail", product.medium));
      const price = el("div", "ProductItem__PriceList Heading");
      price.append(
        el("span", "ProductItem__Price Price Text--subdued", product.label),
      );
      info.append(price);
      wrapper.append(info);
      return item;
    }

    render(requestedPage) {
      const size = Math.max(1, Number(this.dataset.pageSize) || 48);
      const pages = Math.max(1, Math.ceil(this.filtered.length / size));
      const page = Math.max(1, Math.min(pages, Math.floor(requestedPage)));
      const oldGrid = this.results.querySelector(".ProductList");
      if (oldGrid) {
        this.mobile = oldGrid.dataset.mobileCount;
        this.desktop = oldGrid.dataset.desktopCount;
        this.cellClass =
          oldGrid.querySelector(".Grid__Cell")?.className || this.cellClass;
      }
      const wrapper = document.createElement("div");
      wrapper.className = "ProductListWrapper";
      const grid = document.createElement("div");
      grid.className = this.gridClass;
      grid.dataset.mobileCount = this.mobile;
      grid.dataset.desktopCount = this.desktop;
      for (const product of this.filtered.slice(
        (page - 1) * size,
        page * size,
      )) {
        const cell = document.createElement("div");
        cell.className = this.cellClass;
        cell.append(this.card(product));
        grid.append(cell);
      }
      wrapper.append(grid);
      this.results.replaceChildren(wrapper);
      this.status.textContent = `${this.filtered.length.toLocaleString()} ${this.filtered.length === 1 ? "work" : "works"}${this.filtered.length ? ` · Page ${page} of ${pages}` : " match these filters"}.`;
      if (this.filtered.length === 0) {
        const empty = document.createElement("p");
        empty.className = "EmptyState";
        empty.textContent =
          "No artwork matches these filters. Try another budget or artist.";
        this.results.append(empty);
      }
      if (pages > 1) {
        const nav = document.createElement("nav");
        nav.className = "PPABrowse__Pages";
        nav.setAttribute("aria-label", "Artwork result pages");
        for (const [label, step] of [
          ["Previous", -1],
          ["Next", 1],
        ]) {
          const button = document.createElement("button");
          button.type = "button";
          button.className = "Link Link--underline";
          button.textContent = label;
          button.disabled = page + step < 1 || page + step > pages;
          button.addEventListener("click", () => {
            const url = new URL(location.href);
            url.searchParams.set("ppa_page", String(page + step));
            history.pushState({}, "", url);
            this.render(page + step);
            this.scrollIntoView({ block: "start" });
          });
          nav.append(button);
        }
        this.results.append(nav);
      }
    }
  }
  customElements.define("ppa-browse", PPABrowse);
})();
